import { Either, Maybe } from 'monet';

const EXCEL_TYPE_REQUEST = 'application/vnd.ms-excel';
const JSON_TYPE_REQUEST = 'application/json';

/**
 * @description Позиция в отчете
 * @param {string} groupName
 * @param {string} totalPrice
 * @param {string} totalDiscount
 * @param {string} paidAmount
 * @param {string} debt
 * @param {double} summary
 * @param {string} summaryTitle
 * @param {string} groupNameTitle
 * @constructor
 */
function Detail(
  groupName,
  totalPrice,
  totalDiscount,
  paidAmount,
  debt,
  summary,
  summaryTitle,
  groupNameTitle = '',
) {
  this.groupName = groupName;
  this.totalPrice = totalPrice;
  this.totalDiscount = totalDiscount;
  this.paidAmount = paidAmount;
  this.debt = debt;
  this.summary = summary;
  this.summaryTitle = summaryTitle;
  this.groupNameTitle = groupNameTitle;
}

/**
 * @description Позиция в отчете
 * @param {string} groupName
 * @param {string} totalPrice
 * @param {string} totalDiscount
 * @param {string} paidAmount
 * @param {string} debt
 * @param {double} summary
 * @param {string} summaryTitle
 * @param {string} groupNameTitle
 * @param {Array}  details
 * @constructor
 */
function ReportLine(
  groupName,
  totalPrice,
  totalDiscount,
  paidAmount,
  debt,
  summary,
  summaryTitle,
  groupNameTitle = '',
  details = [],
) {
  this.groupName = groupName;
  this.totalPrice = totalPrice;
  this.totalDiscount = totalDiscount;
  this.paidAmount = paidAmount;
  this.debt = debt;
  this.summary = summary;
  this.summaryTitle = summaryTitle;
  this.groupNameTitle = groupNameTitle;
  this.details = details.map((detail) => new Detail(
    detail.group_name,
    detail.total_price,
    detail.total_discount,
    detail.paid_amount,
    detail.debt,
    detail.summary,
    detail.summary_title,
  ));
  this.hasDetails = () => !!details.length;
}

/**
 * @description Шлюз для отчетов
 * @param {Axios} axios
 * @constructor
 */
export default function ReportGateway(axios) {
  // ДАШБОРД ВЛАДЕЛЬЦА КЛИНИКИ
  /**
   * @description Получить данные дашборда
   * @param period
   * @return {Promise<Either<unknown, any>>}
   */
  this.getReportDashboard = (period = []) => axios
    .get('/api/reports/dashboard', {
      params: { period },
    })
    .then(
      (response) => Either.right(response.data.diagrams),
      (error) => {
        if (error.isAxiosError && [400, 403, 404].includes(error.response.status)) {
          return Either.Left(error.response.data);
        }
        throw error;
      },
    );

  // ПО СОТРУДНИКАМ
  /**
   * @description Получить отчет по сотрудникам
   * @return {Promise<any>}
   */
  this.getReportByEmployees = (period = [], positionId = 0) => axios
    .get('/api/reports/by-employees', {
      params: { period, position_id: positionId },
    })
    .then(
      (response) => {
        const employees = response.data.items
          .map((employee) => ({
            firstVisitCount: employee.first_visit_count,
            name: employee.name,
            positionNames: employee.position_names,
            priceSum: employee.visit_services_price_sum,
            secondVisitCount: employee.second_visit_count,
            visitCount: employee.visit_count,
          }));
        return Either.Right(employees);
      },
      (error) => {
        if (error.isAxiosError && [400, 403, 404].includes(error.response.status)) {
          return Either.Left(error.response.data);
        }
        throw error;
      },
    );
  /**
   * @description Получить отчет по сотрудникам
   * @param {Array<String>} period
   * @param {Array<Number>} employeeIds
   * @param {Object}        sort
   * @return {Promise<any>}
   */
  this.getReportByEmployeesWorkload = (period, employeeIds, sort) => axios
    .get('/api/reports/by-employees-workload', {
      params: {
        period,
        employee_ids: employeeIds,
        sort_type: sort.type,
        sort_direction: sort.direction,
      },
    })
    .then(
      (response) => {
        const employees = response.data.items
          .map((employee) => ({
            name: employee.name,
            surnameWithInitials: employee.surname_with_initials,
            specialtyName: employee.specialty_name,
            workHours: employee.work_hours,
            workloadHours: employee.workload_hours,
            workloadPercent: employee.workload_percent,
          }));
        return Either.Right(employees);
      },
      (error) => {
        if (error.isAxiosError && [400, 403, 404].includes(error.response.status)) {
          return Either.Left(error.response.data);
        }
        throw error;
      },
    );
  /**
   * @description Получить новый отчет о выручке по направлениям.
   * @return {Promise<Either<any, {columns: object[], id: string, lines: string[][]}>>}
   */
  this.getDirectionsRevenueReport = (
    base,
    categoryIds,
    employeeIds,
    levels,
    patientIds,
    period,
    periodType,
    sort,
    cashboxIds,
  ) => axios
    .post('/api/directions-revenue-reports', {
      base,
      category_ids: categoryIds,
      employee_ids: employeeIds,
      levels,
      patient_ids: patientIds,
      period,
      period_type: periodType,
      sort,
      cashbox_ids: cashboxIds,
    })
    .then(
      (response) => Either.Right({
        columns: response.data.columns,
        id: response.data.report_id,
        lines: response.data.lines,
      }),
      (error) => {
        if (error.isAxiosError && error.response.status === 400) {
          return Either.Left(error.response.data);
        }
        throw error;
      },
    );
  /**
   * @description Получить опции для отчета о выручке по направлениям.
   * @return {Promise<Maybe<any>>}
   */
  this.getDirectionsRevenueReportOptions = () => axios
    .get('/api/report-filters/directions-revenue')
    .then((response) => Maybe.fromEmpty(response.data));
  /**
   * @description Получить фильтры
   */
  this.getFilterForReportByEmployees = () => axios
    .get('/api/report-filters/by-employees')
    .then(
      (response) => Either.Right(response.data.items),
      (error) => {
        if (error.isAxiosError && [400, 403, 404].includes(error.response.status)) {
          return Either.Left(error.response.data);
        }
        throw error;
      },
    );
  /**
   * @description Получить фильтры для отчета сотрудников
   */
  this.getFilterForReportEmployeesWorkload = () => axios
    .get('/api/report-filters/employees-workload')
    .then(
      (response) => Either.Right(response.data.filters),
      (error) => {
        if (error.isAxiosError && [400, 403, 404].includes(error.response.status)) {
          return Either.Left(error.response.data);
        }
        throw error;
      },
    );

  // ПО АДМИНИСТРАТОРАМ
  /**
   * @description Получить отчет по всем администраторам о пациентах.
   * @param {int[]}   employeeIds Идентификаторы сотрудников.
   * @param {string}  from        Начальная дата.
   * @param {string}  to          Конечная дата.
   * @param {boolean} inPercent   В процентах.
   * @param {string}  sort        Сортировка.
   * @returns {Promise<Either<any, {columns: array, groups: array, lines: array}>>}
   */
  this.getPatientReportByAdministrators = (employeeIds, from, to, inPercent, sort = '') => {
    const params = { from, to };
    if (employeeIds.length) {
      Object.assign(params, { employee_ids: employeeIds });
    }
    if (sort !== '') {
      Object.assign(params, { sort });
    }
    if (inPercent) {
      Object.assign(params, { in_percent: inPercent });
    }
    return axios
      .get('/api/patient-reports/by-administrators', { params })
      .then((response) => {
        const { columns, groups, lines } = response.data;
        return Either.Right({ columns, groups, lines });
      });
  };
  /**
   * @description Получить фильтры для отчета о пациентах по администраторам.
   * @returns {Promise<Maybe<any>>}
   */
  this.getPatientReportFiltersByAdministrators = () => axios
    .get('/api/patient-report-filters/by-administrators')
    .then((response) => Maybe.fromEmpty(response.data));

  // УСЛУГИ ПО ДМС
  /**
   * @description Получить отчет о услугах по дмс.
   * @param {int[]}   employeeIds Идентификаторы сотрудников.
   * @param {int[]}   patientIds  Идентификаторы пациентов.
   * @param {string}  from        Начальная дата.
   * @param {string}  to          Конечная дата.
   * @param {string}  sort        Сортировка.
   * @returns {Promise<Either<any, {columns: array, lines: array}>>}
   */
  this.getServicesByDms = (employeeIds, patientIds, from, to, sort) => {
    const params = { from, to };
    if (employeeIds.length) {
      Object.assign(params, { employee_ids: employeeIds });
    }
    if (patientIds.length) {
      Object.assign(params, { patient_ids: patientIds });
    }
    if (sort !== '') {
      Object.assign(params, { sort });
    }
    return axios
      .get('/api/services-reports/by-dms', { params })
      .then((response) => {
        const { lines, columns } = response.data;
        return Either.Right({ lines, columns });
      });
  };
  /**
   * @description Получить фильтры для отчета о услугах по дмс.
   * @returns {Promise<Maybe<any>>}
   */
  this.getServicesFiltersByDms = () => axios
    .get('/api/services-reports-filters/by-dms')
    .then((response) => Maybe.fromEmpty(response.data));

  // ПО ПРОДАЖАМ ПО СОТРУДНИКАМ
  /**
   * @description Получить отчет по продажам
   * @param {Array} period
   * @param {Array} employeeIds
   * @param {Object} sort
   * @return {Promise}
   */
  this.getReportSalesEmployees = (period, employeeIds, sort) => axios
    .get('/api/sale-reports/by-employees', {
      params: {
        period,
        employee_ids: employeeIds,
        sort_type: sort.type,
        sort_direction: sort.direction,
      },
      headers: { accept: JSON_TYPE_REQUEST },
    })
    .then(
      (response) => {
        const reportLines = response.data.items
          .map((line) => new ReportLine(
            line.group_name,
            line.total_price,
            line.total_discount,
            line.paid_amount,
            line.debt,
            line.summary,
            line.summary_title,
            '',
            line.details,
          ));
        return Either.Right(reportLines);
      },
      (error) => {
        if (error.isAxiosError && [400, 403, 404].includes(error.response.status)) {
          return Either.Left(error.response.data);
        }
        throw error;
      },
    );
  /**
   * @description Получить отчет по продажам в виде таблицы
   * @param {Array} period
   * @param {Array} employeeIds
   * @return {Promise}
   */
  this.getReportSalesEmployeesBySheet = (period, employeeIds) => axios
    .get('/api/sale-reports/by-employees', {
      headers: { accept: EXCEL_TYPE_REQUEST },
      responseType: 'blob',
      params: {
        period,
        employee_ids: employeeIds,
      },
    })
    .then(
      (response) => Either.Right(response.data),
      (error) => {
        if (error.isAxiosError && [400, 403, 404].includes(error.response.status)) {
          return Either.Left(error.response.data);
        }
        throw error;
      },
    );
  /**
   * @description Получить фильтры для отчета сотрудников
   */
  this.getFilterForReportEmployeesSales = () => axios
    .get('/api/report-filters/employees-sales')
    .then(
      (response) => Either.Right(response.data.filters),
      (error) => {
        if (error.isAxiosError && [400, 403, 404].includes(error.response.status)) {
          return Either.Left(error.response.data);
        }
        throw error;
      },
    );

  // ПО ПРОДАЖАМ ПО ИСТОЧНИКАМ
  /**
   * @description Получить фильтры для отчета источников
   */
  this.getFilterForReportSourcesSales = () => axios
    .get('/api/report-filters/sources-sales')
    .then(
      (response) => Either.Right(response.data.filters),
      (error) => {
        if (error.isAxiosError && [400, 403, 404].includes(error.response.status)) {
          return Either.Left(error.response.data);
        }
        throw error;
      },
    );
  /**
   * @description Получить отчет по продажам
   * @param {Array} period
   * @param {Array} sourceNames
   * @param {Object} sort
   * @return {Promise}
   */
  this.getReportSalesSources = (period, sourceNames, sort) => axios
    .get('/api/sale-reports/by-sources', {
      params: {
        period,
        source_names: sourceNames,
        sort_type: sort.type,
        sort_direction: sort.direction,
      },
      headers: { accept: JSON_TYPE_REQUEST },
    })
    .then(
      (response) => {
        const reportLines = response.data.items
          .map((line) => new ReportLine(
            line.group_name,
            line.total_price,
            line.total_discount,
            line.paid_amount,
            line.debt,
            line.summary,
            line.summary_title,
          ));
        return Either.Right(reportLines);
      },
      (error) => {
        if (error.isAxiosError && [400, 403, 404].includes(error.response.status)) {
          return Either.Left(error.response.data);
        }
        throw error;
      },
    );
  /**
   * @description Получить отчет по продажам в виде таблицы
   * @param {Array} period
   * @param {Array} sourceNames
   * @return {Promise}
   */
  this.getReportSalesSourcesBySheet = (period, sourceNames) => axios
    .get('/api/sale-reports/by-sources', {
      headers: { accept: EXCEL_TYPE_REQUEST },
      responseType: 'blob',
      params: {
        period,
        source_names: sourceNames,
      },
    })
    .then(
      (response) => Either.Right(response.data),
      (error) => {
        if (error.isAxiosError && [400, 403, 404].includes(error.response.status)) {
          return Either.Left(error.response.data);
        }
        throw error;
      },
    );

  // ПО ПРОДАЖАМ УСЛУГ
  /**
   * @description Получить отчет по продажам услуг по категориям
   * @param {Array} period
   * @param {Object} sort
   * @return {Promise}
   */
  this.getReportSalesServicesByCategories = (period, sort) => axios
    .get('/api/sale-reports/services/by-categories', {
      params: {
        period,
        sort_type: sort.type,
        sort_direction: sort.direction,
      },
      headers: { accept: JSON_TYPE_REQUEST },
    })
    .then(
      (response) => {
        const reportLines = response.data.items
          .map((line) => new ReportLine(
            line.group_name,
            line.total_price,
            line.total_discount,
            line.paid_amount,
            line.debt,
            line.summary,
            line.summary_title,
          ));
        return Either.Right(reportLines);
      },
      (error) => {
        if (error.isAxiosError && [400, 403, 404].includes(error.response.status)) {
          return Either.Left(error.response.data);
        }
        throw error;
      },
    );
  /**
   * @description Получить отчет по продажам услуг ПО КОНКРЕТНЫМ услугам
   * @param {Array} period
   * @param {Object} sort
   * @param {Array} serviceIds
   * @return {Promise}
   */
  this.getReportSalesServicesByServices = (period, sort, serviceIds) => axios
    .get('/api/sale-reports/services/by-services', {
      params: {
        period,
        service_ids: serviceIds,
        sort_type: sort.type,
        sort_direction: sort.direction,
      },
      headers: { accept: JSON_TYPE_REQUEST },
    })
    .then(
      (response) => {
        const reportLines = response.data.items
          .map((line) => new ReportLine(
            line.group_name,
            line.total_price,
            line.total_discount,
            line.paid_amount,
            line.debt,
            line.summary,
            line.summary_title,
            line.group_name_title,
          ));
        return Either.Right(reportLines);
      },
      (error) => {
        if (error.isAxiosError && [400, 403, 404].includes(error.response.status)) {
          return Either.Left(error.response.data);
        }
        throw error;
      },
    );
  /**
   * @description Получить отчет по продажам в виде таблицы
   * @param {Array} period
   * @return {Promise}
   */
  this.getReportSalesServicesSheetByCategories = (period) => axios
    .get('/api/sale-reports/services/by-categories', {
      headers: { accept: EXCEL_TYPE_REQUEST },
      responseType: 'blob',
      params: { period },
    })
    .then(
      (response) => Either.Right(response.data),
      (error) => {
        if (error.isAxiosError && [400, 403, 404].includes(error.response.status)) {
          return Either.Left(error.response.data);
        }
        throw error;
      },
    );
  /**
   * @description Получить отчет по продажам в виде таблицы
   * @param {Array} period
   * @param {Array} serviceIds
   * @return {Promise}
   */
  this.getReportSalesServicesSheetByServices = (period, serviceIds) => axios
    .get('/api/sale-reports/services/by-services', {
      headers: { accept: EXCEL_TYPE_REQUEST },
      responseType: 'blob',
      params: { period, service_ids: serviceIds },
    })
    .then(
      (response) => Either.Right(response.data),
      (error) => {
        if (error.isAxiosError && [400, 403, 404].includes(error.response.status)) {
          return Either.Left(error.response.data);
        }
        throw error;
      },
    );
  /**
   * @description Получить фильтры по продажам услуг
   */
  this.getFilterForReportByServices = () => axios
    .get('/api/report-filters/services-sales')
    .then(
      (response) => Either.Right(response.data.items),
      (error) => {
        if (error.isAxiosError && [400, 403, 404].includes(error.response.status)) {
          return Either.Left(error.response.data);
        }
        throw error;
      },
    );

  /**
   * @description Получить отчет о выручке по направлениям.
   * @param {string}   reportId
   * @param {boolean}  fullReport
   * @returns {Promise<Either>}
   */
  this.getDirectionsRevenueReportBySheet = (reportId, fullReport) => axios
    .get(
      `/api/directions-revenue-reports/${reportId}`,
      {
        responseType: 'blob',
        params: { full_report: +fullReport },
      },
    )
    .then(
      (response) => Either.Right(response.data),
      (error) => {
        if (error.isAxiosError && [400, 403].includes(error.response.status)) {
          return Either.Left(error.response.data);
        }
        throw error;
      },
    );

  /**
   * @description Получить опции для отчета стоимости привлечения пациента.
   * @return {Promise<Maybe<any>>}
   */
  this.getPatientAttractingCostReportOptions = () => axios
    .get('/api/report-filters/patient-attracting-cost')
    .then((response) => Maybe.fromEmpty(response.data));
  /**
   * Получить отчет стоимости привлечения пациента.
   * @param {string}   from
   * @param {string}   to
   * @param {number[]} sourceIds
   * @param {string}   sort
   * @param {string}   patientFilter
   * @returns {Promise<Either>}
   */
  this.getPatientAttractingCostReport = (from, to, sourceIds, sort, patientFilter) => axios
    .post(
      `/api/reports/patient-attracting-cost/${from}:${to}`,
      { source_ids: sourceIds, sort, filter_by_patient: patientFilter },
    ).then(
      (response) => Either.Right(response.data),
      (error) => {
        if (error.isAxiosError && [400, 403].includes(error.response.status)) {
          return Either.Left(error.response.data);
        }
        throw error;
      },
    );
  /**
   * Получить отчет стоимости привлечения пациента в виде таблицы.
   * @param {string} id
   * @returns {Promise<Either>}
   */
  this.getPatientAttractingCostReportBySheet = (id) => axios
    .get(
      `api/reports/patient-attracting-cost/${id}`,
      {
        headers: { accept: EXCEL_TYPE_REQUEST },
        responseType: 'blob',
      },
    ).then(
      (response) => Either.Right(response.data),
      (error) => {
        if (error.isAxiosError && [400, 403].includes(error.response.status)) {
          return Either.Left(error.response.data);
        }
        throw error;
      },
    );

  /**
   * @description Получить опции для отчета по планам лечения.
   * @return {Promise<Maybe<any>>}
   */
  this.getTreatmentPlansReportOptions = () => axios
    .get('/api/report-filters/treatment-plans')
    .then((response) => Maybe.fromEmpty(response.data));

  /**
   * Получить отчет по планам лечения.
   * @param {string}   from
   * @param {string}   to
   * @param {number[]} employeeIds
   * @param {string}   sort
   * @param {number[]} responsibleEmployeeIds
   * @returns {Promise<Either>}
   */
  this.getTreatmentPlansReport = (from, to, employeeIds, sort, responsibleEmployeeIds) => axios
    .post(
      `/api/reports/treatment-plans/${from}:${to}`,
      { employee_ids: employeeIds, sort, responsible_employee_ids: responsibleEmployeeIds },
    ).then(
      (response) => Either.Right(response.data),
      (error) => {
        if (error.isAxiosError && [400, 403].includes(error.response.status)) {
          return Either.Left(error.response.data);
        }
        throw error;
      },
    );

  /**
   * Получить отчет по планам лечения в виде таблицы.
   * @param {string} id
   * @returns {Promise<Either>}
   */
  this.getTreatmentPlansReportBySheet = (id) => axios
    .get(
      `api/reports/treatment-plans/${id}`,
      {
        headers: { accept: EXCEL_TYPE_REQUEST },
        responseType: 'blob',
      },
    ).then(
      (response) => Either.Right(response.data),
      (error) => {
        if (error.isAxiosError && [400, 403].includes(error.response.status)) {
          return Either.Left(error.response.data);
        }
        throw error;
      },
    );

  /**
   * @description Получить опции для отчета по остаткам товара на складе.
   * @return {Promise<Maybe<any>>}
   */
  this.getWarehouseProductBalancesReportOptions = () => axios
    .get('/api/report-filters/warehouse-product-balances')
    .then((response) => Maybe.fromEmpty(response.data));

  /**
   * Получить отчет по остаткам товара на складе.
   * @param {string}   from
   * @param {string}   to
   * @param {number[]} employeeIds
   * @param {string}   sort
   * @param {number[]} responsibleEmployeeIds
   * @returns {Promise<Either>}
   */
  this.getWarehouseProductBalancesReport = (from, to, sort, match = '') => axios
    .post(
      `/api/reports/warehouse-product-balances/${from}:${to}`,
      { sort, match },
    ).then(
      (response) => Either.Right(response.data),
      (error) => {
        if (error.isAxiosError && [400, 403].includes(error.response.status)) {
          return Either.Left(error.response.data);
        }
        throw error;
      },
    );

  /**
   * Получить отчет по остаткам товара на складе в виде таблицы.
   * @param {string} id
   * @returns {Promise<Either>}
   */
  this.getWarehouseProductBalancesReportBySheet = (id) => axios
    .get(
      `api/reports/warehouse-product-balances/${id}`,
      {
        headers: { accept: EXCEL_TYPE_REQUEST },
        responseType: 'blob',
      },
    ).then(
      (response) => Either.Right(response.data),
      (error) => {
        if (error.isAxiosError && [400, 403].includes(error.response.status)) {
          return Either.Left(error.response.data);
        }
        throw error;
      },
    );

  // ПО ВОЗВРАЩАЕМОСТИ ПАЦИЕНТОВ
  /**
   * @description Получить опции для отчета о возвращаемости пациентов.
   * @return {Promise<Maybe<any>>}
   */
  this.getPatientReturnRateReportOptions = () => axios
    .get('/api/report-filters/patient-return-rate')
    .then((response) => Maybe.fromEmpty(response.data));

  /**
     * @description Получить отчет о возвращаемости пациентов.
     * @param {string}   reportId
     * @returns {Promise<Either>}
     */
  this.getPatientReturnRateReportBySheet = (reportId) => axios
    .get(
      `/api/patient-return-rate-reports/${reportId}`,
      {
        responseType: 'blob',
      },
    )
    .then(
      (response) => Either.Right(response.data),
      (error) => {
        if (error.isAxiosError && [400, 403].includes(error.response.status)) {
          return Either.Left(error.response.data);
        }
        throw error;
      },
    );

  /**
     * @description Получить новый отчет о возвращаемости пациентов.
     * @return {Promise<Either<any, {columns: object[], id: string, lines: string[][]}>>}
     */
  this.getPatientReturnRateReport = (
    employeeIds,
    period,
    periodType,
    inPercentage,
  ) => axios
    .post('/api/patient-return-rate-reports', {
      employee_ids: employeeIds,
      period,
      period_type: periodType,
      in_percentage: inPercentage,
    })
    .then(
      (response) => Either.Right({
        columns: response.data.columns,
        id: response.data.report_id,
        lines: response.data.lines,
      }),
      (error) => {
        if (error.isAxiosError && error.response.status === 400) {
          return Either.Left(error.response.data);
        }
        throw error;
      },
    );
}
